import { generateStampedImages, generatePreviewImage } from "../../services/imageService";
import { dataUrlToBlob, solidStripPng } from "./imaging";

// The images a card is saved with, produced exactly like NewPass does so the
// dashboard can re-open and re-save the card later without breaking it:
//   backgroundImage_0..N  stamp strips with 0..N stamps filled (stamp cards)
//   backgroundImage_N+1   the plain strip, which the editor reloads as the base
//   stamp_icon            the icon the editor redraws stamps with
//   preview_image         the card picture shown in the dashboard
// Rendering needs a full-size (380px) PassPreview mounted off screen: never
// display:none, visibility:hidden or under a CSS transform, all of which make
// html2canvas return blank images.

function withTimeout(promise, ms, message) {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

// `includeStrips: false` skips the strips on an edit that did not change them:
// the server keeps the images it has when an update sends none.
export async function captureCardImages({ captureRoot, stripElement, formData, stampIconBlob, includeStrips = true }) {
  if (!captureRoot) throw new Error("Card preview not mounted");
  try {
    await withTimeout(document.fonts?.ready ?? Promise.resolve(), 3000, "fonts");
  } catch {
    // render with whatever font is ready
  }

  let backgroundImages;
  if (includeStrips) {
    backgroundImages = [];
    if (formData.type === "stamp") {
      if (!stripElement) throw new Error("Stamp strip not mounted");
      const stamped = await withTimeout(
        generateStampedImages(stripElement, Number(formData.stampCount), {
          stampIconColor: formData.stampIconColor,
          backgroundColor: formData.backgroundColor,
        }),
        60000,
        "Card images timed out",
      );
      backgroundImages.push(...stamped);
    }
    backgroundImages.push(await solidStripPng(formData.textColor || "#762105"));
  }

  const container = captureRoot.querySelector(".pass-preview-container");
  if (!container) throw new Error("Card preview container not mounted");
  const previewDataUrl = await withTimeout(generatePreviewImage(container), 30000, "Card preview timed out");

  return {
    backgroundImages,
    stampIcon: includeStrips ? stampIconBlob || null : null,
    previewImage: dataUrlToBlob(previewDataUrl),
  };
}
